import type { Dispatch } from 'react';
import { useLayoutEffect } from 'react';

import type { FocusOverride, QueryBuilderAction } from '../../../types/QueryBuilder';

import type { GridSelectionState } from './useGridSelection';

/**
 * Consumes the focusOverride from state after a mutation and applies focus to
 * the target token and sub-cell. Runs synchronously before paint (useLayoutEffect)
 * so focus is applied before the browser renders the update.
 *
 * Token-level virtual focus is updated via GridSelectionState.
 * Sub-cell DOM focus is applied by querying [data-key][data-part].
 */
export function useApplyFocusOverride(
  focusOverride: FocusOverride | null,
  dispatch: Dispatch<QueryBuilderAction>,
  state: GridSelectionState | null,
  wrapperRef: React.RefObject<HTMLElement | null>,
  allKeys: string[],
) {
  useLayoutEffect(() => {
    if (!focusOverride) {
      return;
    }

    const { itemKey, part } = focusOverride;

    const resolvedKey = itemKey === 'end' ? (allKeys.at(-1) ?? null) : itemKey;

    if (state && resolvedKey !== null) {
      state.setFocusedKey(resolvedKey);
    }

    if (wrapperRef.current && resolvedKey !== null) {
      const tokenEl = wrapperRef.current.querySelector<HTMLElement>(`[data-key="${CSS.escape(resolvedKey)}"]`);
      if (part) {
        // Focus a specific sub-cell (key/op/value) inside a content token.
        tokenEl?.querySelector<HTMLElement>(`[data-part="${CSS.escape(part)}"]`)?.focus();
      } else if (tokenEl) {
        // No part specified — focus the free-text input. For ft-after-N / ft-start
        // keys the data-key lives directly on the FreeText input, so the tokenEl IS
        // the input we want; for content-token containers the input lives as a
        // descendant (LozengeFilter wrappers etc.). Try self-match first, then
        // fall back to a descendant lookup.
        const input =
          tokenEl instanceof HTMLInputElement && !tokenEl.hasAttribute('data-part')
            ? tokenEl
            : tokenEl.querySelector<HTMLInputElement>('input:not([data-part])');
        input?.focus();
      }
    }

    dispatch({ type: 'RESET_FOCUS_OVERRIDE' });
  }, [focusOverride, dispatch, state, wrapperRef, allKeys]);
}
